import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import axios from 'axios';
import './expenses.css';

const ExpenseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    category: 'Other',
    amount: '',
    payment_type: 'cash',
    paid_date: ''
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get('/expenses/categories');
        setCategories(response.data);
      } catch (err) {
        console.error('Failed to fetch expense categories:', err);
      }
    };

    fetchCategories();

    if (isEdit) {
      const fetchExpense = async () => {
        try {
          setLoading(true);

          const response = await axios.get(
            `/expenses/${id}`
          );

          setFormData({
            name: response.data.name,
            category: response.data.category || 'Other',
            amount: response.data.amount,
            payment_type: response.data.payment_type || 'cash',
            paid_date: response.data.paid_date
              ? response.data.paid_date.split('T')[0]
              : ''
          });
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to fetch expense');
        } finally {
          setLoading(false);
        }
      };

      fetchExpense();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      if (isEdit) {
        await axios.put(
          `/expenses/${id}`,
          formData
        );
      } else {
        await axios.post(
          '/expenses',
          formData
        );
      }

      navigate('/expenses');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save expense');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return <div className="loading">Loading expense...</div>;
  }

  return (
    <div className="expense-form-container">
      <div className="expense-form-header">
        <h1 className="expense-form-title">
          {isEdit ? 'Edit Expense' : 'Add New Expense'}
        </h1>

        <button
          type="button"
          className="back-btn"
          onClick={() => navigate('/expenses')}
        >
          <FiArrowLeft />
          <span>Back to Expenses</span>
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="expense-form">

        <div className="form-group">
          <label htmlFor="name">Expense Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. July staff salary, electricity bill"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">Expense Category</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
          >
            {(categories.length ? categories : ['Other']).map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="amount">Amount (Rs)</label>
          <input
            type="number"
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            min="0"
            step="0.01"
            placeholder="Rs"
            required
            onWheel={(e) => e.target.blur()}
          />
        </div>

        <div className="form-group">
          <label htmlFor="payment_type">Payment Method</label>
          <select
            id="payment_type"
            name="payment_type"
            value={formData.payment_type}
            onChange={handleChange}
            required
          >
            <option value="cash">💵 Cash</option>
            <option value="bank">🏦 Bank / Transfer</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="paid_date">Payment Date</label>
          <input
            type="date"
            id="paid_date"
            name="paid_date"
            value={formData.paid_date}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="save-btn"
            disabled={loading}
          >
            <FiSave />
            <span>
              {loading
                ? 'Saving...'
                : isEdit
                ? 'Update Expense'
                : 'Save Expense'}
            </span>
          </button>
        </div>

      </form>
    </div>
  );
};

export default ExpenseForm;
